import { AuditLog } from "../api/admin.api";

const ROLE_LABEL: Record<string, string> = {
  user: "User",
  barista: "Barista",
  admin: "Admin",
  dev: "Developer",
};

const STATUS_LABEL: Record<string, string> = {
  ordered: "รอเตรียม",
  "in-progress": "กำลังเตรียม",
  done: "พร้อมรับ",
  picked: "รับแล้ว",
  rejected: "ปฏิเสธ",
  cancel: "ยกเลิก",
};

const roleLabel = (r?: unknown) =>
  typeof r === "string" ? (ROLE_LABEL[r] ?? r) : "?";
const statusLabel = (s?: unknown) =>
  typeof s === "string" ? (STATUS_LABEL[s] ?? s) : "?";

export interface FormattedAudit {
  icon: string;
  /** Human-readable summary in Thai. */
  summary: string;
  /** Optional small line shown under the summary. */
  detail?: string;
}

/**
 * Convert a raw audit row into a friendly Thai sentence + emoji icon.
 * Falls back gracefully when targetName / metadata are missing.
 */
export function formatAudit(row: AuditLog): FormattedAudit {
  const actor = row.actor?.name ?? `User #${row.actorId}`;
  const meta = (row.metadata ?? {}) as Record<string, unknown>;
  const target = row.targetName
    ?? (row.targetType && row.targetId != null
      ? `${row.targetType} #${row.targetId}`
      : "");

  switch (row.action) {
    case "user.role.change":
      return {
        icon: "🔑",
        summary: `${actor} เลื่อนตำแหน่ง ${target || "ผู้ใช้"} เป็น ${roleLabel(meta.newRole)}`,
      };
    case "user.update":
      return {
        icon: "✏️",
        summary: `${actor} แก้ไขข้อมูลของ ${target || "ผู้ใช้"}`,
      };
    case "user.delete":
      return {
        icon: "🗑️",
        summary: `${actor} ลบบัญชี ${target || "ผู้ใช้"}`,
      };
    case "user.password.reset.admin":
      return {
        icon: "🔐",
        summary: `${actor} รีเซ็ตรหัสผ่านของ ${target || "ผู้ใช้"}`,
      };

    case "menu.create":
      return { icon: "➕", summary: `${actor} เพิ่มเมนู ${target || ""}`.trim() };
    case "menu.update":
      return { icon: "✏️", summary: `${actor} แก้ไขเมนู ${target || ""}`.trim() };
    case "menu.delete":
      return { icon: "🗑️", summary: `${actor} ลบเมนู ${target || ""}`.trim() };

    case "category.create":
      return { icon: "📂", summary: `${actor} เพิ่มหมวดหมู่ ${target || ""}`.trim() };
    case "category.update":
      return { icon: "📂", summary: `${actor} แก้ไขหมวดหมู่ ${target || ""}`.trim() };
    case "category.delete":
      return { icon: "🗑️", summary: `${actor} ลบหมวดหมู่ ${target || ""}`.trim() };

    case "broadcast.create":
      return {
        icon: "📣",
        summary: `${actor} ส่งประกาศใหม่`,
        detail: typeof meta.text === "string" ? `"${meta.text}"` : undefined,
      };
    case "broadcast.update":
      return { icon: "📣", summary: `${actor} แก้ไขประกาศ ${target || ""}`.trim() };
    case "broadcast.delete":
      return { icon: "🗑️", summary: `${actor} ลบประกาศ ${target || ""}`.trim() };

    case "cafe.open":
      return { icon: "☕", summary: `${actor} เปิดร้าน Cafe` };
    case "cafe.close":
      return { icon: "🌙", summary: `${actor} ปิดร้าน Cafe` };
    case "bar.open":
      return { icon: "🍸", summary: `${actor} เปิดร้าน Bar` };

    case "order.status.change": {
      const note = typeof meta.reject_note === "string" && meta.reject_note ? meta.reject_note : undefined;
      return {
        icon: "📋",
        summary: `${actor} เปลี่ยนสถานะออเดอร์ #${row.targetId ?? "?"} เป็น ${statusLabel(meta.status)}`,
        detail: note ? `เหตุผล: ${note}` : undefined,
      };
    }
    case "order.delete":
      return { icon: "🗑️", summary: `${actor} ลบออเดอร์ #${row.targetId ?? "?"}` };

    case "settings.update": {
      const parts: string[] = [];
      if (typeof meta.dailyOrderLimit === "number") {
        parts.push(`Daily order limit = ${meta.dailyOrderLimit}`);
      } else if (meta.dailyOrderLimit === null) {
        parts.push("Daily order limit (clear)");
      }
      return {
        icon: "⚙️",
        summary: `${actor} แก้ไขการตั้งค่าระบบ`,
        detail: parts.join(" · ") || undefined,
      };
    }

    default:
      return {
        icon: "•",
        summary: `${actor} → ${row.action}${target ? ` · ${target}` : ""}`,
      };
  }
}
